/**
 * The research-topic bar, declared on the route (`handle.header`) rather than pushed into redux from
 * `getHeaderConfig` after data resolves — so it renders at navigation time with nothing loaded (APP-3564).
 */

import { useParams } from "react-router-dom"
import { capitalize, map, omit } from "lodash"
import { Gradient } from "@mui/icons-material"
import { useAppSelector } from "hooks/redux"
import HomeHeader from "modules/home-header"
import ContextualMenu from "components/contextual-menu"
import ResourceTooltipInfo from "components/resource-tooltip-info"
import { computeSecurityInfo } from "helpers/topics"
import { getCurrentTopic, getModel, getPortfolioInfo } from "./selectors"
import { getContextualMenuItems } from "./selectors/menu"

interface InfoItem {
  value?: unknown
  label?: string
  values?: { label?: string, value?: unknown }[]
}

function buildInfo(topic: ReturnType<typeof getCurrentTopic>, portfolioInfo: ReturnType<typeof getPortfolioInfo>): InfoItem[] {
  if (!topic) { return [] }

  const info: InfoItem[] = [
    { value: topic.description },
    { label: "Selected Date", value: topic.selectedDate },
  ]

  if (portfolioInfo) {
    info.push({
      label: "Reference Instrument",
      values: [{ label: "Name", value: portfolioInfo.name }],
    })
  } else if (topic.referenceInstrument?.type !== "PORTFOLIO") {
    // Security topic: show ticker/exchange/country from the inline securityId object.
    // Skip when type is PORTFOLIO but portfolioInfo is null — portfolios are still loading.
    const securityInfo = computeSecurityInfo(topic.referenceInstrument?.securityId)
    info.push({
      label: "Reference Instrument",
      values: map(omit(securityInfo, "internalId"), (value, key) => ({
        label: `${capitalize(key)}:`,
        value,
      })),
    })
  }

  return info
}

interface Props {
  account: string
}

export default function ResearchTopicHeader({ account }: Props) {
  const params = useParams()
  // Value or null: the slot still holds the previous topic while the next one loads, and this bar now
  // renders through that window.
  const topic = useAppSelector(state => getCurrentTopic(state, { params }))
  const portfolioInfo = useAppSelector(getPortfolioInfo)
  const menuItems = useAppSelector(getContextualMenuItems)
  const modelId = useAppSelector(getModel)

  const info = buildInfo(topic, portfolioInfo)

  return <HomeHeader account={account} Icon={Gradient} title={topic ? topic.name : "Loading Research Topic..."} modelId={modelId}>
    {info.length > 0 && <div className="info-tooltip">
      <ResourceTooltipInfo info={info} alias={undefined} label={undefined} />
    </div>}
    {topic && <ContextualMenu items={menuItems} />}
  </HomeHeader>
}
